import { useContext, useMemo, useState, useEffect } from "react";
import { useAuth } from "./AuthProvider";
import { useExponea } from "./ExponeaProvider";
import { isBrowser, getCookie, gaCustomEvent, getSessionUserId } from "@helpers/browser";
import { hashEmail } from "@helpers/general";
import { getShopifyStoreByCountry } from "../../locale-shopifies";
import { GeoContext } from "./GeoProvider";

import {
  gtmSendEvent,
  gtmSendAddToCart,
  gtmSendAddToWishlist,
  gtmSendRemoveFromCart,
  gtmSendProductClick,
  gtmSendViewCollectionPage,
  gtmSendViewProductPage,
  gtmSendViewCart,
  gtmSendSearchEvent,
  gtmSendSignUp,
} from "./tracking/trackingGTM";

import {
  expSendViewProductPage,
  expSendAddToWishlist,
  expSendRemoveFromWishlist,
  expSendAccountError,
  expSendUserSignup,
  expSendUserLogin,
  expSendSideCartOpen,
  expSendMenuClick,
  expSendCollectionFilterClick,
  expSendCollectionSortClick,
  expSendFloatingAddToCart,
  expTrackCollectionCards,
  expTrackSubscriptionCancel,
  expTrackSubscriptionSkip,
  expTrackSubscriptionPause,
  expTrackSubscriptionReactivate,
  expTrackAddToCart,
  expTrackRecommendedProduct,
  expTrackClickAlternative,
  expOptionalProductsDrawerOpen,
  expOptionalProductsDrawerAdd,
  expOptionalProductsDrawerRemove,
  expOptionalProductsDrawerEdit,
  expOptionalProductsDrawerConfirmation,
  expTrackCountryUpdate,
  expTrackRemoveFromCart,
  expTrackAddFreeGift,
  expCancelOrder,
  expSendAddToCartFromVto,
  expTrackSubscriptionRefund,
  expRecommendedProductsPopupAction,
  expRecommendedProductsATC,
  expTrackBYOBCartActions,
  expTrackBYOBAddToCart,
  expTrackBYOBFiltersActions,
  expTrackSwapFreeGift,
  extTrackDowngradeSubscriptionCancel,
  expTrackDowngradeSubscriptionPopup,
  extTrackDowngradeSubscriptionConfirmation,
  expTrackBYOBPopupActions,
  expTrackGiftPackagingActions,
} from "./tracking/trackingExponea";

import {
  pinSendViewProductPage,
  pinSendAddToCart,
  pinSendPageVisit,
  pinSendViewCategory,
} from "./tracking/trackingPinterest";

import {
  algoliaConvertedObjectIDsAfterSearch,
  algoliaProductClickAfterSearch,
  algoliaProductAddedToCart,
} from "./tracking/trackingAlgolia";

import {
  elevarSendViewCart,
  elevarSendAddToCart,
  elevarSendRemoveFromCart,
  elevarSendViewPLP,
  elevarSendSearch,
  elevarSendLogin,
  elevarSendSignUp,
  elevarSendPageView,
  elevarViewProduct,
} from "./tracking/trackingElevar";

import {
  bingSendAddToCart,
  bingViewCollection,
  bingViewProduct,
  bingViewCart,
} from "./tracking/trackingBing";

import { retentionSendPageView } from "./tracking/trackingRetention";

import {
  blotoutSendPageView,
  blotoutSendAddToCart,
  blotoutViewProduct,
  blotoutSearch,
  blotoutSendRemoveFromCart,
} from "./tracking/trackingBlotout";

import { twPageView, twContact, twAddToCart } from "./tracking/trackingTripleWhale";

import { ComponentType } from "../constants/ComponentType";

export async function sendUserSignIn(email) {
  try {
    const hashedEmail = await hashEmail(email);
    gtmSendEvent("user_signin", { user_email: hashedEmail || "" });
  } catch (err) {
    console.error("sendUserSignIn failed", err);
  }
}

function hasAddressRequiredFields(address) {
  // These fields are required when sending the address
  const requiredFields = ["first_name", "last_name", "postal_code", "country"];
  const addressKeys = Object.keys(address);
  const hasRequiredFields = requiredFields.every((field) => addressKeys.includes(field));

  return hasRequiredFields;
}

/**
 * Email and address have to be global variables (set on window)
 * If address doesn't have all required fields, it shouldn't be set at all
 * Required fields for address: 'first_name', 'last_name', 'postal_code', 'country'
 * If a field is undefined, it shouldn't be set at all
 */
const prepareDataForGADS = (accountDetails, exponeaEmail) => {
  const userData = {
    email: accountDetails?.email || (exponeaEmail !== "null" && exponeaEmail),
    first_name: accountDetails?.firstName,
    last_name: accountDetails?.lastName,
    country: accountDetails?.defaultAddress?.countryCodeV2,
    region: accountDetails?.defaultAddress?.province,
    city: accountDetails?.defaultAddress?.city,
    postal_code: accountDetails?.defaultAddress?.zip,
  };

  const relevantUserDataAddress = {};

  if (isBrowser) {
    if (window.email === undefined && userData.email !== undefined) {
      window.email = userData.email;

      // If a field is undefined, we shouldn't set it
      Object.keys(userData).map((key) => {
        if (!!userData[key] && key !== "email") {
          relevantUserDataAddress[key] = userData[key];
        }
      });

      if (hasAddressRequiredFields(relevantUserDataAddress)) {
        window.address = relevantUserDataAddress;
      }
    }
  }
};

export function getPageViewTrackings() {
  return [
    pinSendPageVisit,
    elevarSendPageView,
    retentionSendPageView,
    blotoutSendPageView,
    twPageView,
  ];
}

export function useTracking() {
  const [hashedEmail, setHashedEmail] = useState("");
  const { accountDetails } = useAuth();
  const { dataExponea } = useExponea();

  // Get email from Exponea data
  const exponeaEmail = dataExponea?.email_id;

  useEffect(() => {
    (async () => {
      const emailToHash = accountDetails?.email || (exponeaEmail !== "null" && exponeaEmail);

      if (emailToHash) {
        const hashed = await hashEmail(emailToHash);
        setHashedEmail(hashed || "");
      } else {
        // Reset hashedEmail when user logs out
        setHashedEmail("");
      }
    })();
  }, [accountDetails?.email, exponeaEmail]);

  const { gePriceDetails } = useContext(GeoContext);

  const currency = gePriceDetails?.CurrencyCode;

  const api = useMemo(() => {
    const appendGlobalDataGTM = (data) => ({
      ...data,
      user_email: hashedEmail,
      ...(currency ? { currency } : {}),
    });
    prepareDataForGADS(accountDetails, exponeaEmail);

    const shopifyStore = getShopifyStoreByCountry(gePriceDetails?.CountryCode);
    const userToken = getCookie(`${shopifyStore.name}-accessToken`) || getSessionUserId();
    return {
      trackSearch: ({ query, searchResults }) => {
        gtmSendEvent("search", appendGlobalDataGTM({ search_query: query }));
        gtmSendSearchEvent({ query });
        elevarSendSearch({
          products: searchResults.map((p) => p.shopify).slice(0, 15),
          accountDetails,
          hashedEmail,
        });
        blotoutSearch({ query, searchResults });
      },
      trackVtoATC: (products) => {
        expSendAddToCartFromVto(products);
      },
      trackAddToCart: ({
        price,
        title,
        variantId,
        productId,
        sku,
        category,
        currency,
        quantity,
        productHandle,
        shouldTrackAlgolia,
        index,
        source,
        images,
        checkoutUrl,
      }) => {
        gtmSendAddToCart(
          { price, title, productId, category, currency, quantity },
          appendGlobalDataGTM
        );
        pinSendAddToCart({ category, currency, price, productId, variantId, quantity, title });
        elevarSendAddToCart({
          category,
          currency,
          price,
          productId,
          variantId,
          quantity,
          title,
          sku,
          accountDetails,
          hashedEmail,
          images,
        });
        if (shouldTrackAlgolia) {
          algoliaProductAddedToCart(userToken, index, productHandle);
        }
        if (source === ComponentType.FLOATING_ATC) {
          expSendFloatingAddToCart({
            price,
            title,
            variantId,
            productId,
            sku,
            category,
            currency,
          });
        }
        bingSendAddToCart({ productId, currency, price, quantity });
        blotoutSendAddToCart({
          price,
          title,
          variantId,
          productId,
          category,
          currency,
          quantity,
          productHandle,
          images,
          checkoutUrl,
        });
        twAddToCart({ productId, variantId, qty: quantity });
      },
      trackAddToCartExponea: ({
        product_ids,
        product_id,
        product_list,
        sku_ids,
        sku_list,
        variant_ids,
        variant_list,
        total_price,
        total_price_local_currency,
        total_quantity,
        image_list,
        linesStr,
        component_name,
        category,
        bundle,
      }) => {
        expTrackAddToCart({
          product_ids,
          product_id,
          product_list,
          sku_ids,
          sku_list,
          variant_ids,
          variant_list,
          total_price,
          total_price_local_currency,
          total_quantity,
          image_list,
          linesStr,
          component_name,
          category,
          bundle,
        });
      },
      trackRemoveFromCart: ({
        lineItem,
        price,
        title,
        productId,
        product_ids,
        variantId,
        category,
        quantity,
        checkoutUrl,
        productHandle,
      }) => {
        gtmSendRemoveFromCart({ price, title, productId, category, currency, quantity });

        elevarSendRemoveFromCart({
          lineItem,
          title,
          productId,
          category,
          currency,
          quantity,
          accountDetails,
          hashedEmail,
        });

        expTrackRemoveFromCart({
          productId,
          product_ids,
          title,
          price,
          currency,
          category,
          quantity,
        });
        blotoutSendRemoveFromCart({
          price,
          title,
          productId,
          variantId,
          category,
          currency,
          quantity,
          checkoutUrl,
          productHandle,
        });
      },
      trackProductClick: ({ price, title, productId, category }) => {
        gtmSendProductClick({ price, title, productId, category });
      },
      trackViewCollectionPage: ({ collectionId, url, products }) => {
        const collectionIdCopy = collectionId.replace("gid://shopify/Collection/", "");
        gtmSendViewCollectionPage(
          { currency, collectionId: collectionIdCopy, url, products },
          appendGlobalDataGTM
        );
        pinSendViewCategory();
        elevarSendViewPLP({ url, products: products.slice(0, 15), accountDetails, hashedEmail });
        bingViewCollection({ collectionId: collectionIdCopy, products });
      },
      trackViewProductPage: ({ price, title, productType, productId, product }) => {
        gtmSendViewProductPage({ price, title, productType, productId }, appendGlobalDataGTM);
        expSendViewProductPage({ price, title, productType, productId });
        pinSendViewProductPage({ title, productId, productType, price });
        elevarViewProduct({ product, accountDetails, hashedEmail });
        bingViewProduct({ product });
        blotoutViewProduct({ product });
      },
      trackAddToWishlist: ({ type, productHandle, productName, price, productId }) => {
        gtmSendAddToWishlist(
          { productId, price, currency, type, productName },
          appendGlobalDataGTM
        );

        expSendAddToWishlist({ productHandle, price, productId });
      },
      trackRemoveFromWishlist: ({ productHandle, price, productId }) => {
        expSendRemoveFromWishlist({ productHandle, price, productId });
      },
      trackUserSignup: (customerEmail, httpStatus, data) => {
        expSendUserSignup({ email: customerEmail, httpStatus, data });
        gtmSendSignUp();
        elevarSendSignUp({ email: customerEmail, hashedEmail });
        twContact(customerEmail);
      },
      trackUserLogin: (customerEmail) => {
        expSendUserLogin({ email: customerEmail });
        elevarSendLogin({ email: customerEmail, hashedEmail });
        twContact(customerEmail);
      },
      trackCountryUpdate: (country) => {
        expTrackCountryUpdate({ country });
      },
      trackRecommendedProduct(
        action,
        sectionTitle,
        { productId, productTitle, productCategory, testName }
      ) {
        expTrackRecommendedProduct(action, {
          sectionTitle,
          productTitle,
          productId,
          productCategory,
          testName,
        });
      },
      trackOpenSideCart(cart) {
        expSendSideCartOpen({ cart, currency });
        gtmSendViewCart({ cart, currency });
        elevarSendViewCart({ cart, currency, accountDetails, hashedEmail });
        bingViewCart({ cart, currency });
      },
      trackClickAlternative: ({
        page,
        activeProductHandle,
        activeProductColor,
        alternativeHandle,
        alternativeColor,
      }) => {
        expTrackClickAlternative({
          page,
          activeProductHandle,
          activeProductColor,
          alternativeHandle,
          alternativeColor,
        });
      },
      trackProductClickAfterSearch: (indexName, queryId, objectID, position) => {
        // TODO: dl_select_item
        algoliaProductClickAfterSearch(userToken, indexName, queryId, [objectID], [position]);
      },
      trackAddToCartAfterSearch: (index, queryID, objectID) => {
        algoliaConvertedObjectIDsAfterSearch(userToken, index, queryID, objectID);
      },
      trackMenuClick: ({
        destinationUrl,
        menuTitle,
        categoryTitle,
        subCategoryTitle,
        sourceType,
      }) => {
        const eventLabel = subCategoryTitle
          ? `${menuTitle} -> ${categoryTitle} -> ${subCategoryTitle}`
          : `${menuTitle} -> ${categoryTitle}`;
        gaCustomEvent("Menu Link", "Click", eventLabel);
        expSendMenuClick({
          destinationUrl,
          menuTitle,
          categoryTitle,
          subCategoryTitle,
          sourceType,
        });
      },
      trackCollectionFilterClick: (category, title, turningOn) => {
        expSendCollectionFilterClick({ category, title, turningOn });
      },
      trackCollectionSortClick: (value) => {
        expSendCollectionSortClick({ value });
      },
      trackPLPCardClick: ({ collectionHandle, link, title }) => {
        expTrackCollectionCards({
          collectionHandle,
          link,
          title,
        });
      },
      trackSubscriptionCancel: (reason) => {
        expTrackSubscriptionCancel(reason);
      },
      trackSubscriptionSkip: () => {
        expTrackSubscriptionSkip();
      },
      trackSubscriptionPause: (interval) => {
        expTrackSubscriptionPause(interval);
      },
      trackSubscriptionReactivate: () => {
        expTrackSubscriptionReactivate();
      },
      trackSubscriptionRefund: ({ orderId, amount }) => {
        expTrackSubscriptionRefund({ orderId, amount });
      },
      trackOptionalProductsDrawerOpen: ({ originProductTitle }) => {
        expOptionalProductsDrawerOpen({ originProductTitle });
      },
      trackOptionalProductsDrawerAdd: ({ originProductTitle, handle, price, currency }) => {
        expOptionalProductsDrawerAdd({ originProductTitle, handle, price, currency });
      },
      trackOptionalProductsDrawerRemove: ({ originProductTitle, handle, price, currency }) => {
        expOptionalProductsDrawerRemove({ originProductTitle, handle, price, currency });
      },
      trackOptionalProductsDrawerEdit: ({ originProductTitle, addedHandles, price, currency }) => {
        expOptionalProductsDrawerEdit({ originProductTitle, addedHandles, price, currency });
      },
      trackOptionalProductsDrawerConfirmation: ({
        originProductTitle,
        handles,
        price,
        currency,
      }) => {
        expOptionalProductsDrawerConfirmation({ originProductTitle, handles, price, currency });
      },
      trackAccountErrors: ({ action, errorMessage, userEmail }) => {
        const customerEmail = userEmail || hashedEmail;
        expSendAccountError({ action, errorMessage, email: customerEmail });
      },
      trackAddFreeGift: ({ productId, productTitle, productHandle, type }) => {
        expTrackAddFreeGift({
          productId,
          productTitle,
          productHandle,
          type,
        });
      },
      trackSwapFreeGift: () => {
        expTrackSwapFreeGift();
      },
      trackCancelOrder: ({ orderId, reason, status }) => {
        expCancelOrder({ orderId, reason, status });
      },
      trackRecommendedProductsPopupAction: ({ type }) => {
        expRecommendedProductsPopupAction({ type });
      },
      trackRecommendedProductsATC: ({ type, handles }) => {
        expRecommendedProductsATC({ type, handles });
      },
      trackByobCartActions: ({
        action,
        productHandle,
        collectionHandle,
        noOfProducts,
        allSavings,
      }) => {
        expTrackBYOBCartActions({
          action,
          productHandle,
          collectionHandle,
          noOfProducts,
          allSavings,
        });
      },
      trackByobAddToCart: ({
        productsHandles,
        productsVariantsIds,
        collectionHandle,
        savings,
        subtotal,
        total,
      }) => {
        expTrackBYOBAddToCart({
          productsHandles,
          collectionHandle,
          productsVariantsIds,
          savings,
          subtotal,
          total,
        });
      },
      trackByobFiltersActions: ({ action, filters, changedFilter }) => {
        expTrackBYOBFiltersActions({ action, filters, changedFilter });
      },
      trackDowngradeSubscriptionPopup: ({ action, subscriptionPrice }) => {
        expTrackDowngradeSubscriptionPopup({ action, subscriptionPrice });
      },
      trackDowngradeSubscriptionConfirmation: ({ subscriptionPrice }) => {
        extTrackDowngradeSubscriptionConfirmation({ subscriptionPrice });
      },
      trackDowngradeSubscriptionCancel: () => {
        extTrackDowngradeSubscriptionCancel();
      },
      trackingByobPopupActions: ({ action, productHandle, collectionHandle }) => {
        expTrackBYOBPopupActions({ action, productHandle, collectionHandle });
      },
      trackGiftPackagingActions: ({ action, productId }) => {
        expTrackGiftPackagingActions({ action, productId });
      },
    };
  }, [hashedEmail, currency, accountDetails, exponeaEmail, gePriceDetails?.CountryCode]);
  return api;
}
